import { memo, useCallback, useMemo, useState, type CSSProperties } from 'react';
import styled from '@emotion/styled';
import { Avatar, ComboBox } from 'components';
import { ClickAwayListener } from '@mui/base';
import { TableTooltip } from './TableTooltip';
import type { Response } from 'types';
import type { ComboBoxOption } from 'components/inputs/types';
import type { EditableDropdownCellProps } from '../types';
import { useUser } from 'features/auth';
import { useEscapeToClose } from '../hooks';
import { useEditableContextOptional } from './OptimizedTableProvider';
import { clearEditOnSuccess } from '../utils/inlineEditUtils';
import { t } from '@lingui/macro';
import {
  DROPDOWN_CELL_BASE,
  DROPDOWN_CELL_HOVER,
  DROPDOWN_CELL_EDITING,
  DROPDOWN_CELL_ERROR,
  DROPDOWN_CELL_ERROR_TEXT,
  DROPDOWN_CELL_DISABLED,
  DROPDOWN_CELL_DISABLED_TEXT,
} from './dropdownInlineEditStyles';

/**
 * Resolve API vars if they are a function (for context-dependent lookups)
 * This allows features to pass function-based vars in the registry that get
 * resolved with row context at render time (e.g., supplier contact persons)
 */
const resolveApiVars = (
  api: { vars?: string[] | ((ctx?: unknown) => string[]) },
  context: { row: { original: unknown } },
  userData: unknown
): string[] => {
  if (!api.vars) return [];
  if (typeof api.vars === 'function') {
    return api.vars({ userData, rowData: context.row.original });
  }
  return api.vars;
};

/**
 * EditableDropdownCell - A self-contained inline-editable dropdown cell for tables
 *
 * Provides:
 * - Click-to-edit functionality
 * - Searchable dropdown with API integration
 * - Avatar + label display in read mode
 * - No dependencies on legacy table components
 *
 * @example
 * ```tsx
 * <EditableDropdownCell
 *   context={cellContext}
 *   api={{
 *     url: apiBrands.get.search,
 *     key: 'brands',
 *     search: true,
 *     responseTransform: (res) => res.data.map(b => ({ id: b.id, label: b.name }))
 *   }}
 *   value={row.brand?.name}
 *   onChange={(e, value) => updateData(rowIndex, { brandId: value.id })}
 *   editableRowIndex={editableRowIndex}
 *   editableColumnId={editableColumnId}
 * />
 * ```
 */
const EditableDropdownCellInner = <T extends { id: number | string }>({
  context,
  api,
  value,
  onChange,
  tooltipTitle,
  editableRowIndex: editableRowIndexProp,
  editableColumnId: editableColumnIdProp,
  groupBy = false,
  avatarColor,
  showAvatar = true,
  onCellEdit: onCellEditProp,
  onClickAway: onClickAwayProp,
  required,
  error: errorProp,
  disabled: disabledProp,
}: EditableDropdownCellProps<T>) => {
  const { data: userData } = useUser();
  const [isHovered, setIsHovered] = useState(false);
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const editableRowIndex = editableRowIndexProp ?? editableState?.rowIndex ?? null;
  const editableColumnId = editableColumnIdProp ?? editableState?.columnId ?? null;
  const isEditing =
    context.row.index === editableRowIndex && context.column.id === editableColumnId;

  const onCellEdit =
    onCellEditProp ??
    (() => setEditableState({ rowIndex: context.row.index, columnId: context.column.id }));
  const onClickAway =
    onClickAwayProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const wrappedOnChange = useCallback(
    (
      e: React.SyntheticEvent,
      selectedValue: ComboBoxOption | ComboBoxOption[] | null | string,
      reason: string
    ) => {
      const result = onChange?.(e, selectedValue, reason);
      clearEditOnSuccess(result ?? undefined, () =>
        setEditableState({ rowIndex: null, columnId: null })
      );
    },
    [onChange, setEditableState]
  );

  // Resolve vars if they are a function (minimal change to support dynamic vars)
  const resolvedVars = resolveApiVars(api, context, userData);

  const handleClick = useCallback(() => {
    if (!isEditing && !disabledProp && context.row.original.id !== -1) {
      onCellEdit?.(context);
    }
  }, [isEditing, disabledProp, context, onCellEdit]);

  const handleClickAway = useCallback(() => {
    // Clear edit state when clicking away without selecting
    onClickAway?.();
  }, [onClickAway]);

  useEscapeToClose(isEditing, handleClickAway);

  const hasError = errorProp ?? false;
  const showHover = isHovered && !isEditing && !disabledProp;

  const cellStateStyle: CSSProperties = useMemo(
    () => ({
      ...DROPDOWN_CELL_BASE,
      ...(showHover && !hasError ? DROPDOWN_CELL_HOVER : {}),
      ...(showHover && hasError ? { backgroundColor: DROPDOWN_CELL_HOVER.backgroundColor } : {}),
      ...(disabledProp ? DROPDOWN_CELL_DISABLED : {}),
      ...(isEditing ? DROPDOWN_CELL_EDITING : {}),
      ...(hasError && !isEditing ? DROPDOWN_CELL_ERROR : {}),
    }),
    [showHover, hasError, disabledProp, isEditing]
  );

  const errorOrDisabledColor = hasError
    ? (DROPDOWN_CELL_ERROR_TEXT.color as string)
    : disabledProp
      ? (DROPDOWN_CELL_DISABLED_TEXT.color as string)
      : undefined;

  // Memoize read mode content to prevent unnecessary re-renders
  const readModeContent = useMemo(() => {
    if (!value) {
      return (
        <PlaceholderText style={errorOrDisabledColor ? { color: errorOrDisabledColor } : undefined}>
          _
        </PlaceholderText>
      );
    }
    return showAvatar ? (
      <AvatarLabel label={value} color={avatarColor} />
    ) : (
      <Label style={errorOrDisabledColor ? { color: errorOrDisabledColor } : undefined}>
        {value}
      </Label>
    );
  }, [showAvatar, value, avatarColor, errorOrDisabledColor]);

  // Memoize the response transform function
  const responseTransform = useCallback(
    (data: unknown) => {
      const options = api
        .responseTransform(data as Response<{ id: number; name: string; avatarColor?: string }[]>)
        .map(item => ({
          ...item,
          CustomElement: showAvatar ? AvatarLabel : undefined,
          customProps: showAvatar
            ? {
                label: item.label,
                color: item.color,
              }
            : undefined,
        }));
      // Add "Clear" option at the top when there's a value (but not for required fields)
      if (value && !required) {
        const clearOption = {
          id: '__clear__',
          label: t`Clear`,
          CustomElement: undefined,
          customProps: undefined,
        };
        return [clearOption, ...options];
      }
      return options;
    },
    [api, showAvatar, value, required]
  );

  // Memoize the API config object
  const apiConfig = useMemo(
    () => ({
      ...api,
      vars: resolvedVars,
      responseTransform,
    }),
    [api, resolvedVars, responseTransform]
  );

  return (
    <CellWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={cellStateStyle}
    >
      {isEditing ? (
        <ClickAwayListener onClickAway={handleClickAway}>
          <EditorContainer>
            <ComboBox
              id={context.column.id}
              noOptionsText="No options"
              api={apiConfig}
              open
              groupBy={groupBy}
              value={value}
              onChange={wrappedOnChange}
            />
          </EditorContainer>
        </ClickAwayListener>
      ) : (
        <CellContent
          onClick={handleClick}
          tooltipTitle={isEditing ? undefined : tooltipTitle || value}
        >
          {readModeContent}
        </CellContent>
      )}
    </CellWrapper>
  );
};

// Wrap with memo for performance optimization
export const EditableDropdownCell = memo(EditableDropdownCellInner) as <
  T extends { id: number | string },
>(
  props: EditableDropdownCellProps<T>
) => JSX.Element;

/**
 * AvatarLabel - Display component for dropdown items and read mode
 * Shows an avatar with initials and a label
 */
export const AvatarLabel = ({ label, color }: { label: string; color?: string }) => {
  if (!label) return null;
  return (
    <>
      <Avatar letters={label || ''} size="xs" color={color} />
      <Spacing />
      <Label>{label}</Label>
    </>
  );
};

/**
 * ReadOnlyDropdownCell - A read-only version of the dropdown cell
 * Has the same layout/styling as EditableDropdownCell but without click interaction
 */
export const ReadOnlyDropdownCell = ({
  value,
  showAvatar = true,
  avatarColor,
}: {
  value: string;
  showAvatar?: boolean;
  avatarColor?: string;
}) => {
  const content = showAvatar ? (
    <AvatarLabel label={value || ''} color={avatarColor} />
  ) : (
    <Label>{value || ''}</Label>
  );

  return (
    <TableTooltip title={value || ''}>
      <CellContentContainer isEditable={false}>{content}</CellContentContainer>
    </TableTooltip>
  );
};

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.75rem;
  line-height: 1rem;
`;

const PlaceholderText = styled.span`
  color: ${p => p.theme.palette.text.placeholder ?? '#96999d'};
  font-style: normal;
  font-size: 0.75rem;
  line-height: 1rem;
`;

const Spacing = styled.span`
  min-width: 15px;
  height: 15px;
`;

const CellWrapper = styled.div``;

const EditorContainer = styled.div`
  width: 100%;

  .MuiAutocomplete-popper {
    min-width: 300px;
  }
  .MuiAutocomplete-root {
    width: 100%;
  }
  .MuiAutocomplete-endAdornment {
    display: none;
  }
  && .MuiInput-root {
    padding-right: 0;
  }
`;

const CellContentInner = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const CellContent = ({
  children,
  tooltipTitle,
  onClick,
}: {
  children: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  onClick?: () => void;
}) => {
  return tooltipTitle ? (
    <TableTooltip title={tooltipTitle}>
      <CellContentInner onClick={onClick}>{children}</CellContentInner>
    </TableTooltip>
  ) : (
    <CellContentInner onClick={onClick}>{children}</CellContentInner>
  );
};

const CellContentContainer = styled.div<{ isEditable?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${p => (p.isEditable ? 'pointer' : 'auto')};
  min-height: 40px;
  width: 100%;

  .MuiAutocomplete-popper {
    min-width: 300px;
  }
  .MuiAutocomplete-root {
    width: 100%;
  }
  .MuiAutocomplete-endAdornment {
    display: none;
  }
  && .MuiInput-root {
    padding-right: 0;
  }
`;

const CellContainer = ({
  children,
  tooltipTitle,
  onClick,
  isEditable,
}: {
  children: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  onClick?: () => void;
  isEditable?: boolean;
}) => {
  return tooltipTitle ? (
    <TableTooltip title={tooltipTitle}>
      <CellContentContainer onClick={onClick} isEditable={isEditable}>
        {children}
      </CellContentContainer>
    </TableTooltip>
  ) : (
    <CellContentContainer onClick={onClick} isEditable={isEditable}>
      {children}
    </CellContentContainer>
  );
};
